import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Plus, Edit, Trash2 } from 'lucide-react';

const emptyForm = {
  symbol: '', name: '', decimals: 2, enabled: true,
  deposit_enabled: true, withdrawal_enabled: true, transfer_enabled: true, swap_enabled: true,
  icon_url: null
};

export const AdminCurrenciesPage = () => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await publicAPI.getCurrencies(false);
      setCurrencies(data);
    } catch (error) {
      toast.error('Failed to load currencies');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (currency) => {
    setEditingId(currency.id);
    setForm({
      symbol: currency.symbol,
      name: currency.name,
      decimals: currency.decimals,
      enabled: currency.enabled,
      deposit_enabled: currency.deposit_enabled,
      withdrawal_enabled: currency.withdrawal_enabled,
      transfer_enabled: currency.transfer_enabled,
      swap_enabled: currency.swap_enabled,
      icon_url: currency.icon_url
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await adminAPI.updateCurrency(editingId, form);
        toast.success('Currency updated');
      } else {
        await adminAPI.createCurrency(form);
        toast.success('Currency created');
      }
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this currency? This cannot be undone.')) return;
    try {
      await adminAPI.deleteCurrency(id);
      toast.success('Currency deleted');
      load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Delete failed');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-currencies-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Currencies</h1>
            <p className="mt-2 text-muted-foreground">Manage platform currencies</p>
          </div>
          <Button onClick={openCreate} data-testid="create-currency-button">
            <Plus className="h-4 w-4 mr-1" /> Add Currency
          </Button>
        </div>

        <Card>
          <CardHeader><CardTitle>All Currencies</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8">Loading...</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Decimals</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Features</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currencies.map((c) => (
                    <TableRow key={c.id} data-testid={`currency-row-${c.symbol}`}>
                      <TableCell className="font-mono font-semibold">{c.symbol}</TableCell>
                      <TableCell>{c.name}</TableCell>
                      <TableCell>{c.decimals}</TableCell>
                      <TableCell>
                        <Badge variant={c.enabled ? 'default' : 'secondary'} className={c.enabled ? 'bg-green-600' : ''}>
                          {c.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {c.deposit_enabled && <Badge variant="outline" className="text-xs">Deposit</Badge>}
                          {c.withdrawal_enabled && <Badge variant="outline" className="text-xs">Withdraw</Badge>}
                          {c.transfer_enabled && <Badge variant="outline" className="text-xs">Transfer</Badge>}
                          {c.swap_enabled && <Badge variant="outline" className="text-xs">Swap</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-currency-${c.symbol}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(c.id)} data-testid={`delete-currency-${c.symbol}`}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Currency' : 'Add Currency'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Symbol</Label>
                  <Input
                    value={form.symbol}
                    onChange={(e) => setForm({...form, symbol: e.target.value.toUpperCase()})}
                    placeholder="USD"
                    required
                    disabled={!!editingId}
                    data-testid="currency-symbol-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Decimals</Label>
                  <Input
                    type="number" min="0" max="18"
                    value={form.decimals}
                    onChange={(e) => setForm({...form, decimals: parseInt(e.target.value)})}
                    required
                    data-testid="currency-decimals-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="US Dollar"
                  required
                  data-testid="currency-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <Label className="cursor-pointer">Enabled</Label>
                  <Switch checked={form.enabled} onCheckedChange={(v) => setForm({...form, enabled: v})} />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <Label className="cursor-pointer">Deposit</Label>
                  <Switch checked={form.deposit_enabled} onCheckedChange={(v) => setForm({...form, deposit_enabled: v})} />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <Label className="cursor-pointer">Withdrawal</Label>
                  <Switch checked={form.withdrawal_enabled} onCheckedChange={(v) => setForm({...form, withdrawal_enabled: v})} />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <Label className="cursor-pointer">Transfer</Label>
                  <Switch checked={form.transfer_enabled} onCheckedChange={(v) => setForm({...form, transfer_enabled: v})} />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border col-span-2">
                  <Label className="cursor-pointer">Swap</Label>
                  <Switch checked={form.swap_enabled} onCheckedChange={(v) => setForm({...form, swap_enabled: v})} />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-currency-button">{editingId ? 'Update' : 'Create'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};
